import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Globe, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CompanyDialog } from "./CompanyDialog";
import { useConfirm } from "@/components/ConfirmDialog";

export function CompaniesTab() {
  const { askConfirm, confirmDialog } = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [companyDialog, setCompanyDialog] = useState(null);

  const { data: companies = [] } = useQuery({
    queryKey: ["admin-companies"],
    queryFn: async () => (await api.get("/admin/companies")).data,
  });

  const companyDeleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/companies/${id}`),
    onSuccess: () => { toast.success(t("admin.toastCompanyDeleted")); qc.invalidateQueries({ queryKey: ["admin-companies"] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">{t("admin.tabCompanies")}</CardTitle>
          <Button size="sm" onClick={() => setCompanyDialog("new")} data-testid="admin-new-company-button"><Building2 className="mr-2 h-4 w-4" /> {t("admin.newCompany")}</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader><TableRow><TableHead>{t("admin.thCode")}</TableHead><TableHead>{t("admin.thLegalName")}</TableHead><TableHead>{t("admin.thContact")}</TableHead><TableHead>{t("admin.thWebsite")}</TableHead><TableHead>{t("admin.thType")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {companies.map((c) => (
                <TableRow key={c.id} data-testid={`admin-company-row-${c.id}`}>
                  <TableCell><span className="font-mono text-sm font-bold" data-testid={`company-code-${c.id}`}>{c.code || "—"}</span></TableCell>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-sm">
                    {c.contact_person && <div>{c.contact_person}</div>}
                    {c.contact_email && <div className="text-xs text-slate-500">{c.contact_email}</div>}
                    {c.contact_phone && <div className="text-xs text-slate-500">{c.contact_phone}</div>}
                    {!c.contact_person && !c.contact_email && !c.contact_phone && <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                  <TableCell>
                    {c.website ? (
                      <a href={c.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-sky-700 hover:underline" data-testid={`company-website-${c.id}`}>
                        <Globe className="h-3.5 w-3.5" /> {c.website.replace(/^https?:\/\//, "")}
                      </a>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                  <TableCell>{c.is_placeholder ? <Badge variant="outline" className="font-mono text-xs bg-slate-100">{t("badges.placeholder")}</Badge> : <Badge variant="outline" className="font-mono text-xs">{t("badges.active")}</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setCompanyDialog(c)} data-testid={`company-edit-${c.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-rose-600" onClick={() => askConfirm(t("admin.deleteCompanyConfirm", { name: c.name }), () => companyDeleteMut.mutate(c.id))} data-testid={`company-delete-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CompanyDialog dialog={companyDialog} onClose={() => setCompanyDialog(null)} />
      {confirmDialog}
    </>
  );
}
